import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// logger

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// order-entry
import { OEModelMasterService } from "./oe-model-master.service";
import {
  OELockingProfileModel,
  OEInputLockStateItemModel,
} from "../../data/models/oe-locking-profile.model";
import {
  INPUT_TO_PANEL_MAPPING,
  MODIFY_PARTIAL_LOCK_MAPPING,
  MODIFY_PARTIAL_ACTIVE_LOCK_MAPPING,
  PRIVATIZATION_PRIVATE_COMMENTS_LOCK_MAPPING,
  MODIFY_FULL_LOCK_MAPPING,
  MODIFY_LOOP_LOCK_ACTIVE_MAPPING,
  MODIFY_ROOT_TERMINAL_ACTIVE_LEG_LOCK_MAPPING,
  MODIFY_INFO_ACTIVE_LOCK_MAPPING,
  MODIFY_INFO_PASSIVE_LOCK_MAPPING,
  MODIFY_INFO_TERMINAL_LOCK_MAPPING,
  AMEND_MARKUP_LOCK_MAPPING,
  PRIVATIZATION_BOTH_COMMENTS_LOCK_MAPPING,
  PRIVATIZATION_CLIENT_COMMENTS_LOCK_MAPPING,
  PRIVATIZATION_NO_COMMENTS_LOCK_MAPPING,
  NO_PRIVATIZATION_UNLOCK_MAPPING,
  MODIFY_LOOP_LOCK_PASSIVE_MAPPING,
  NO_LOCKING_INPUT,
  BFX_PRIVATIZATION_INPUT_CONTROL,
  OCO_LOCK_MAPPING,
  IF_DONE_LOCK_MAPPING,
  LOOP_LOCK_MAPPING,
  TIMEOUT_LOCK_MAPPING,
  MKT_ORDER_LOCK_MAPPING,
  MKT_STRATEGY_LEG_LOCK_MAPPING,
  NEW_ENTRY_LOCK_MAPPING,
  GAMMA_CUSTOMISE_LOCK_MAPPING,
  GAMMA_CUSTOMISE_UNLOCK_MAPPING,
  BULKED_LOCK_MAPPING,
} from "../../data/mappings/oe-lock-mapping.const";
import { OENLockedState } from "../../data/enums/oe-locked-state.enum";
import { OENLockProfileType } from "../../data/enums/oe-modify-lock-profile-type.enum";
import { OENInputControl } from "../../data/enums/oe-input-control.enum";
import { OEModelGetterService } from "./oe-model-getter.service";
import { OESettingsService } from "../basic/oe-settings.service";
import { OENLockingProfileType } from "../../data/enums/oe-locking-profile-type.enum";
import { OEDataModel } from "../../data/models/oe-data.model";

@Injectable()
export class OEModelLockingService {
  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private master: OEModelMasterService,
    private getter: OEModelGetterService,
    private settings: OESettingsService,
  ) {}

  /**********************************************************************/
  /* CONSTRUCTOR*/

  public processLockingOnModifyInfo(refId: string, issuerOrderId: string): void {
    if (this.master.has(refId)) {
      let issuerRefId = this.getter.findRefIdForOrderId(issuerOrderId);
      //let rootRefId = this.getter.findRefIdForOrderId(refId);
      if (this.getter.isSrvOrderStateTerminal(refId)) {
        // Strategy node is in terminal state
        this.createLockProfileForModification(refId, OENLockProfileType.MODIFY_INFO_TERMINAL_LOCK);
      } else {
        if (issuerRefId === refId) {
          // active leg
          this.createLockProfileForModification(refId, OENLockProfileType.MODIFY_INFO_ACTIVE_LOCK);
        } else {
          this.createLockProfileForModification(refId, OENLockProfileType.MODIFY_INFO_PASSIVE_LOCK);
        }
      }
    } else {
      logger.out.error(
        `ERROR: _oeModel.processLockingOnModifyInfo(): model with refId: '${refId}' is invalid in master!`,
      );
    }
  }

  public processLockingOnModify(refId: string, rootOrderId: string, issuerOrderId: string): void {
    if (this.master.has(refId)) {
      let issuerRefId = this.getter.findRefIdForOrderId(issuerOrderId);
      let rootRefId = this.getter.findRefIdForOrderId(rootOrderId);
      let parentRefId = this.getter.getParentRef(refId);
      let siblingLoopRefId = this.getter.hasSiblingLoop(refId)
        ? this.getter.getSiblingLoopRefId(refId)
        : null;
      if (this.getter.isSrvOrderStateTerminal(refId)) {
        // Strategy node is in terminal state
        this.createLockProfileForModification(refId, OENLockProfileType.FULL_LOCK);
      } else if (
        this.getter.hasBeenPartiallyFilled(refId) ||
        this.getter.hasBeenFullyFilled(refId)
      ) {
        // Strategy node has been filled
        if (this.getter.hasBeenFullyFilled(refId) && siblingLoopRefId != null) {
          // handling LOOP
          if (issuerRefId === refId) {
            // active leg
            this.createLockProfileForModification(refId, OENLockProfileType.LOOP_ACTIVE_LOCK);
          } else {
            // passive leg
            this.createLockProfileForModification(refId, OENLockProfileType.LOOP_PASSIVE_LOCK);
          }
        } else {
          // no LOOP
          if (issuerRefId === refId) {
            // active leg
            this.createLockProfileForModification(refId, OENLockProfileType.PARTIAL_ACTIVE_LOCK);
          } else {
            // passive leg
            this.createLockProfileForModification(refId, OENLockProfileType.PARTIAL_LOCK);
          }
        }
      } else if (
        siblingLoopRefId != null &&
        (this.getter.hasBeenFullyFilled(siblingLoopRefId) ||
          this.getter.hasBeenPartiallyFilled(siblingLoopRefId))
      ) {
        // Strategy node has LOOP relationship that has been filled
        if (issuerRefId === refId) {
          // active leg
          this.createLockProfileForModification(refId, OENLockProfileType.LOOP_ACTIVE_LOCK);
        } else {
          // passive leg
          this.createLockProfileForModification(refId, OENLockProfileType.LOOP_PASSIVE_LOCK);
        }
      } else if (
        issuerRefId === refId &&
        (this.getter.hasBeenFullyFilled(rootRefId) || this.getter.isInTerminalState(rootRefId))
      ) {
        // Strategy node (not root) on which modify was invoked when strategy root was filled
        this.createLockProfileForModification(refId, OENLockProfileType.NO_ROOT_ACTIVE_LEG_LOCK);
      }
    } else {
      logger.out.error(
        `ERROR: _oeModel.processLockingOnModify(): model with refId: '${refId}' is invalid in dataMap!`,
      );
    }
  }

  public processLockingForPrivatization(refId: string): void {
    if (this.master.has(refId)) {
      this.createLockProfileForPrivatization(refId, this.getter.isModificationPrivatized(refId));
    }
  }

  public createLockingProfileForNewEntry(refId: string): void {
    if (this.master.has(refId)) {
      let lockProfile: OELockingProfileModel = new OELockingProfileModel();
      for (let inputControlItem in OENInputControl) {
        if (
          !isNaN(Number(inputControlItem)) &&
          !(<any>OENInputControl[inputControlItem] instanceof Function) &&
          NEW_ENTRY_LOCK_MAPPING.get(Number(inputControlItem)) != null
        ) {
          let inputControlNum = Number(inputControlItem);
          lockProfile.updateInputLockState(
            new OEInputLockStateItemModel(
              INPUT_TO_PANEL_MAPPING.get(inputControlNum),
              inputControlNum,
              NEW_ENTRY_LOCK_MAPPING.get(inputControlNum),
            ),
          );
        }
      }

      if (this.master.get(refId).strategy != null) {
        this.master.get(refId).strategy.lockProfile = lockProfile;
      } else {
        logger.out.warn(
          `ERROR: _oeModel.createLockingProfileForNewEntry(): Model ${refId} does not have strategy object!`,
        );
      }
    }
  }

  public createLockProfileForBulking(
    refId: string,
    isBulking: boolean,
  ): OELockingProfileModel | null {
    if (this.master.has(refId)) {
      let lockProfile: OELockingProfileModel = new OELockingProfileModel();

      for (let inputControlItem in OENInputControl) {
        if (
          !isNaN(Number(inputControlItem)) &&
          !(<any>OENInputControl[inputControlItem] instanceof Function) &&
          OENInputControl[Number(inputControlItem)] !== "_MULTI_"
        ) {
          let lockStateItem: OEInputLockStateItemModel | null;
          let inputControlNum = Number(inputControlItem);
          if (NO_LOCKING_INPUT.includes(inputControlNum)) {
            lockStateItem = new OEInputLockStateItemModel(
              INPUT_TO_PANEL_MAPPING.get(inputControlNum),
              inputControlNum,
              OENLockedState.NONE,
            );
          } else {
            lockStateItem = new OEInputLockStateItemModel(
              INPUT_TO_PANEL_MAPPING.get(inputControlNum),
              inputControlNum,
              isBulking ? BULKED_LOCK_MAPPING.get(inputControlNum) : OENLockedState.UNLOCKED,
            );
          }
          lockProfile.updateInputLockState(lockStateItem);
        }
      }

      if (this.master.get(refId).strategy != null) {
        this.master.get(refId).strategy.lockProfile = lockProfile;
      } else {
        logger.out.error(
          `ERROR: _oeModel.createLockProfileForBulked(): Model ${refId} does not have strategy!`,
        );
      }
      return lockProfile;
    }

    return null;
  }

  public createLockProfileForStrategyModel(
    refId: string,
    strategyType: CubiqTS.OrderRelationshipType,
  ): void {
    if (this.master.has(refId)) {
      let lockProfile: OELockingProfileModel = new OELockingProfileModel();
      let errorRepeatPrevent: boolean = true;
      for (let inputControlItem in OENInputControl) {
        if (
          !isNaN(Number(inputControlItem)) &&
          !(<any>OENInputControl[inputControlItem] instanceof Function) &&
          OENInputControl[Number(inputControlItem)] !== "_MULTI_"
        ) {
          let lockStateItem: OEInputLockStateItemModel | null;
          let inputControlNum = Number(inputControlItem);
          switch (strategyType) {
            case CubiqTS.OrderRelationshipType.NONE: // unlock all
              if (NO_LOCKING_INPUT.includes(inputControlNum)) {
                lockStateItem = new OEInputLockStateItemModel(
                  INPUT_TO_PANEL_MAPPING.get(inputControlNum),
                  inputControlNum,
                  OENLockedState.NONE,
                );
              } else {
                lockStateItem = new OEInputLockStateItemModel(
                  INPUT_TO_PANEL_MAPPING.get(inputControlNum),
                  inputControlNum,
                  OENLockedState.UNLOCKED,
                );
              }
              break;
            case CubiqTS.OrderRelationshipType.OCO:
              lockStateItem = new OEInputLockStateItemModel(
                INPUT_TO_PANEL_MAPPING.get(inputControlNum),
                inputControlNum,
                OCO_LOCK_MAPPING.get(inputControlNum),
              );
              break;
            case CubiqTS.OrderRelationshipType.IFDONE:
              lockStateItem = new OEInputLockStateItemModel(
                INPUT_TO_PANEL_MAPPING.get(inputControlNum),
                inputControlNum,
                IF_DONE_LOCK_MAPPING.get(inputControlNum),
              );
              break;
            case CubiqTS.OrderRelationshipType.LOOP:
              lockStateItem = new OEInputLockStateItemModel(
                INPUT_TO_PANEL_MAPPING.get(inputControlNum),
                inputControlNum,
                LOOP_LOCK_MAPPING.get(inputControlNum),
              );
              break;
            case CubiqTS.OrderRelationshipType.ON_TIMEOUT:
              lockStateItem = new OEInputLockStateItemModel(
                INPUT_TO_PANEL_MAPPING.get(inputControlNum),
                inputControlNum,
                TIMEOUT_LOCK_MAPPING.get(inputControlNum),
              );
              break;
            default:
              errorRepeatPrevent
                ? logger.out.error(
                    `ERROR: _oeModel.createLockProfileForStrategyModel(): Unknown strategy type, building neutral locking profile for model: ${refId}`,
                  )
                : (errorRepeatPrevent = false);
              lockStateItem = new OEInputLockStateItemModel(
                INPUT_TO_PANEL_MAPPING.get(inputControlNum),
                inputControlNum,
                OENLockedState.NONE,
              );
              break;
          }
          lockProfile.updateInputLockState(lockStateItem);
        }
      }
      if (this.master.get(refId).strategy != null) {
        this.master.get(refId).strategy.lockProfile = lockProfile;
      } else {
        //TODO change log to warn later on
        logger.out.error(
          `ERROR: _oeModel.createLockProfileForStrategy(): Model ${refId} does not have strategy!`,
        );
      }
    }
  }

  public createLockProfileForPrivatization(refId, hasPrivatization: boolean): void {
    if (this.master.has(refId)) {
      if (this.master.get(refId) != null) {
        let lockProfile = this.master.get(refId).privatizationLockProfile;
        let map;

        if (
          hasPrivatization &&
          this.settings.privatizeClientComments &&
          this.settings.privatizePrivateComments
        ) {
          map = PRIVATIZATION_BOTH_COMMENTS_LOCK_MAPPING;
          lockProfile.hasPrivatization = true;
        } else if (hasPrivatization && this.settings.privatizeClientComments) {
          map = PRIVATIZATION_CLIENT_COMMENTS_LOCK_MAPPING;
          lockProfile.hasPrivatization = true;
        } else if (hasPrivatization && this.settings.privatizePrivateComments) {
          map = PRIVATIZATION_PRIVATE_COMMENTS_LOCK_MAPPING;
          lockProfile.hasPrivatization = true;
        } else if (hasPrivatization) {
          map = PRIVATIZATION_NO_COMMENTS_LOCK_MAPPING;
          lockProfile.hasPrivatization = true;
        } else {
          map = NO_PRIVATIZATION_UNLOCK_MAPPING;
          lockProfile.hasPrivatization = false;
        }

        for (let inputControlNum of BFX_PRIVATIZATION_INPUT_CONTROL) {
          let lockStateItem = new OEInputLockStateItemModel(
            INPUT_TO_PANEL_MAPPING.get(inputControlNum),
            inputControlNum,
            map.get(inputControlNum),
          );
          lockProfile.updateInputLockState(lockStateItem);
        }

        this.master.get(refId).privatizationLockProfile = lockProfile;
      }
    }
  }

  public createLockForCustomiseGamma(refId: string): OELockingProfileModel {
    let gammaProfile: OELockingProfileModel = null;
    if (this.master.has(refId)) {
      if (this.master.get(refId).strategy != null) {
        // create the profile
        //
        /*
         */

        let lockProfile: OELockingProfileModel = this.master.get(refId).strategy.lockProfile;
        GAMMA_CUSTOMISE_LOCK_MAPPING.forEach((value: OENLockedState, key: OENInputControl) => {
          lockProfile.updateInputLockState(
            new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
          );

          gammaProfile = new OELockingProfileModel();
          gammaProfile.profile = lockProfile.getLockProfile();
          gammaProfile.profileType = lockProfile.getLockingProfileType();
        });

        return gammaProfile;
      }
    }
  }

  public createUnLockForCustomiseGamma(refId: string): OELockingProfileModel {
    let gammaProfile: OELockingProfileModel = null;
    if (this.master.has(refId)) {
      if (this.master.get(refId).strategy != null) {
        // create the profile
        //
        /*
         */

        let lockProfile: OELockingProfileModel = this.master.get(refId).strategy.lockProfile;
        GAMMA_CUSTOMISE_UNLOCK_MAPPING.forEach((value: OENLockedState, key: OENInputControl) => {
          lockProfile.updateInputLockState(
            new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
          );

          gammaProfile = new OELockingProfileModel();
          gammaProfile.profile = lockProfile.getLockProfile();
          gammaProfile.profileType = lockProfile.getLockingProfileType();
        });

        return gammaProfile;
      }
    }
  }

  public createLockProfileForModification(
    refId: string,
    lockProfileType: OENLockProfileType,
    applyOnAllLegs: boolean = false,
  ): void {
    if (this.master.has(refId)) {
      if (this.master.get(refId).strategy != null) {
        // create the profile
        let lockProfile: OELockingProfileModel = this.master.get(refId).strategy.lockProfile;
        switch (lockProfileType) {
          case OENLockProfileType.PARTIAL_LOCK:
            MODIFY_PARTIAL_LOCK_MAPPING.forEach((value: OENLockedState, key: OENInputControl) => {
              lockProfile.updateInputLockState(
                new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
              );
            });
            break;
          case OENLockProfileType.PARTIAL_ACTIVE_LOCK:
            MODIFY_PARTIAL_ACTIVE_LOCK_MAPPING.forEach(
              (value: OENLockedState, key: OENInputControl) => {
                lockProfile.updateInputLockState(
                  new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
                );
              },
            );
            break;
          case OENLockProfileType.FULL_LOCK:
            MODIFY_FULL_LOCK_MAPPING.forEach((value: OENLockedState, key: OENInputControl) => {
              lockProfile.updateInputLockState(
                new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
              );
            });
            break;
          case OENLockProfileType.LOOP_ACTIVE_LOCK:
            MODIFY_LOOP_LOCK_ACTIVE_MAPPING.forEach(
              (value: OENLockedState, key: OENInputControl) => {
                lockProfile.updateInputLockState(
                  new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
                );
              },
            );
            break;
          case OENLockProfileType.LOOP_PASSIVE_LOCK:
            MODIFY_LOOP_LOCK_PASSIVE_MAPPING.forEach(
              (value: OENLockedState, key: OENInputControl) => {
                lockProfile.updateInputLockState(
                  new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
                );
              },
            );
            break;
          case OENLockProfileType.NO_ROOT_ACTIVE_LEG_LOCK:
            MODIFY_ROOT_TERMINAL_ACTIVE_LEG_LOCK_MAPPING.forEach(
              (value: OENLockedState, key: OENInputControl) => {
                lockProfile.updateInputLockState(
                  new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
                );
              },
            );
            break;
          case OENLockProfileType.MODIFY_INFO_ACTIVE_LOCK:
            MODIFY_INFO_ACTIVE_LOCK_MAPPING.forEach(
              (value: OENLockedState, key: OENInputControl) => {
                lockProfile.updateInputLockState(
                  new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
                );
              },
            );
            break;
          case OENLockProfileType.MODIFY_INFO_PASSIVE_LOCK:
            MODIFY_INFO_PASSIVE_LOCK_MAPPING.forEach(
              (value: OENLockedState, key: OENInputControl) => {
                lockProfile.updateInputLockState(
                  new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
                );
              },
            );
            break;
          case OENLockProfileType.MODIFY_INFO_TERMINAL_LOCK:
            MODIFY_INFO_TERMINAL_LOCK_MAPPING.forEach(
              (value: OENLockedState, key: OENInputControl) => {
                lockProfile.updateInputLockState(
                  new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
                );
              },
            );
            break;
          case OENLockProfileType.AMEND_MARKUP_LOCK:
            AMEND_MARKUP_LOCK_MAPPING.forEach((value: OENLockedState, key: OENInputControl) => {
              lockProfile.updateInputLockState(
                new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
              );
            });
            break;

          default:
            logger.out.error(
              `ERROR: _oeModel.createLockProfileForModification(): unknown lock profile type = ${lockProfileType}`,
            );
            break;
        }

        // Applying the profile to all legs
        if (this.master.hasStrategy(refId) && applyOnAllLegs) {
          this.getter.getStrategyNodesListRoot(refId).forEach((itemRefId: string) => {
            if (this.master.get(itemRefId).strategy != null) {
              this.master.get(itemRefId).strategy.lockProfile = lockProfile;
            } else {
              logger.out.error(
                `ERROR: _oeModel.createLockProfileForModification(): Could not create locking profile for '${refId}', strategy is null!`,
              );
            }
          });
        } else {
          this.master.get(refId).strategy.lockProfile = lockProfile;
        }
      } else {
        logger.out.error(
          `ERROR: _oeModel.createLockProfileForModification(): Could not create locking profile for '${refId}', strategy is null!`,
        );
      }
    }
  }

  public createLockProfileForMKTOrder(refId: string): OELockingProfileModel {
    if (this.getter.has(refId)) {
      if (
        (this.getter.isEntryTypeSpot(refId) || this.getter.isEntryTypeNdf(refId)) &&
        this.getter.isOrderTypeMKT(refId)
      ) {
        let lockingProfile: OELockingProfileModel = new OELockingProfileModel();
        lockingProfile.setLockingProfileType(OENLockingProfileType.TEMPORARY);
        MKT_ORDER_LOCK_MAPPING.forEach((value: OENLockedState, key: OENInputControl) => {
          if (key === OENInputControl.VALUE_DATE && this.getter.isCcyPairFwdEnabled(refId)) {
            // if ccyPair is forward enabled, apply the persistent locking (should be unlocked on root, locked on strategy leg)
            let persistentValueDateLockItem = this.getter
              .get(refId)
              .strategy.lockProfile.getLockProfileForInputElement(OENInputControl.VALUE_DATE);
            if (persistentValueDateLockItem instanceof OEInputLockStateItemModel) {
              lockingProfile.updateInputLockState(persistentValueDateLockItem);
            } else {
              lockingProfile.updateInputLockState(
                new OEInputLockStateItemModel(
                  INPUT_TO_PANEL_MAPPING.get(key),
                  key,
                  OENLockedState.UNLOCKED,
                ),
              );
            }
          } else {
            lockingProfile.updateInputLockState(
              new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
            );
          }
        });
        return lockingProfile;
      }
    }
    return null;
  }

  public createLockProfileForMKTStrategyLeg(refId: string): OELockingProfileModel {
    if (this.getter.has(refId)) {
      if (this.getter.isEntryTypeSpot(refId) || this.getter.isEntryTypeNdf(refId)) {
        if (this.getter.isOrderTypeMKT(refId)) {
          return this.createLockProfileForMKTOrder(refId);
        } else if (this.getter.doesStrategyHaveMKTLeg(refId)) {
          let lockingProfile: OELockingProfileModel = new OELockingProfileModel();
          lockingProfile.setLockingProfileType(OENLockingProfileType.TEMPORARY);
          MKT_STRATEGY_LEG_LOCK_MAPPING.forEach((value: OENLockedState, key: OENInputControl) => {
            lockingProfile.updateInputLockState(
              new OEInputLockStateItemModel(INPUT_TO_PANEL_MAPPING.get(key), key, value),
            );
          });
          return lockingProfile;
        }
      }
    }
    return null;
  }

  public cloneProfile(lockingProfile: OELockingProfileModel): OELockingProfileModel {
    if (lockingProfile instanceof OELockingProfileModel) {
      return this.cloneProfileParameters(lockingProfile);
    } else {
      return new OELockingProfileModel();
    }
  }

  private cloneProfileParameters(obj: any): any {
    let copy;
    // Handle the 3 simple types, and null or undefined
    if (obj == null || typeof obj != "object") return obj;
    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }
    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.cloneProfileParameters(obj[i]);
      }
      return copy;
    }
    // Handle Object
    if (obj instanceof Object) {
      if (obj instanceof OELockingProfileModel) {
        copy = new OELockingProfileModel();
      } else if (obj instanceof OEInputLockStateItemModel) {
        copy = new OEInputLockStateItemModel(obj.panel, obj.input, obj.lockState);
      }
      if (copy != null) {
        if (!(copy instanceof OEInputLockStateItemModel)) {
          for (let attr in obj) {
            if (obj.hasOwnProperty(attr)) {
              copy[attr] = this.cloneProfileParameters(obj[attr]);
            }
          }
        }
        return copy;
      }
    }
  }
}
